import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/services/db";
import {
  addClassic,
  classicFileName,
  classicsUrl,
  fetchClassicsCatalog,
  removeClassic,
  type ClassicEntry,
} from "@/services/classicsService";

// Classics — an in-app "app store for free books". Browses the self-hosted
// public-domain catalog (public/classics/, built by
// scripts/build-classics-catalog.mjs) and adds/removes books from the local
// library with one tap. This is the main way to get books on the public
// build, where file Import is disabled — and a nice shortcut on the full
// build too.
export function ClassicsView() {
  const [catalog, setCatalog] = useState<ClassicEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [subject, setSubject] = useState<string | null>(null);
  // Per-book in-flight ops (download or delete) so each card can show its
  // own spinner without blocking the rest of the store.
  const [busyIds, setBusyIds] = useState<ReadonlySet<number>>(new Set());
  // Books whose add/remove just failed — shows a brief inline note.
  const [failedId, setFailedId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchClassicsCatalog()
      .then((entries) => {
        if (!cancelled) setCatalog(entries);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Live set of library fileNames. Classics import as "<id>.epub", so
  // membership is a simple lookup — and because this is a Dexie live
  // query, cards update instantly when books are added/removed anywhere
  // in the app (including the library screen).
  const libraryFileNames = useLiveQuery(
    async () => {
      const books = await db.books.toArray();
      return new Set(books.map((b) => b.fileName));
    },
    [],
    undefined as Set<string> | undefined,
  );

  // Subject chips, most-common first. Derived once per catalog load.
  const subjects = useMemo(() => {
    if (!catalog) return [];
    const counts = new Map<string, number>();
    for (const e of catalog) {
      for (const s of e.subjects) counts.set(s, (counts.get(s) ?? 0) + 1);
    }
    return [...counts.entries()]
      .filter(([, n]) => n >= 3)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([s]) => s);
  }, [catalog]);

  const visible = useMemo(() => {
    if (!catalog) return [];
    const q = query.trim().toLowerCase();
    return catalog.filter((e) => {
      if (subject && !e.subjects.includes(subject)) return false;
      if (!q) return true;
      return (
        e.title.toLowerCase().includes(q) ||
        e.author.toLowerCase().includes(q) ||
        (e.originalTitle ?? "").toLowerCase().includes(q) ||
        (e.langLabel ?? "").toLowerCase().includes(q)
      );
    });
  }, [catalog, query, subject]);

  // The multilingual "in the original" editions get their own pinned
  // section above the main grid; the main grid stays English-only.
  const multilingual = useMemo(() => visible.filter((e) => !!e.lang), [visible]);
  const english = useMemo(() => visible.filter((e) => !e.lang), [visible]);

  const onToggle = async (entry: ClassicEntry, inLibrary: boolean) => {
    if (busyIds.has(entry.id)) return;
    setFailedId(null);
    setBusyIds((prev) => new Set(prev).add(entry.id));
    try {
      if (inLibrary) {
        // Instant and reversible — no confirm dialog. Adding it back is
        // one tap, and reading progress is keyed by content SHA so it
        // even survives a remove/re-add.
        await removeClassic(entry);
      } else {
        await addClassic(entry);
      }
    } catch {
      setFailedId(entry.id);
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(entry.id);
        return next;
      });
    }
  };

  return (
    <div className="pv-fg relative flex h-full flex-col overflow-y-auto">
      <header
        className="pv-border flex items-center gap-4 border-b px-5"
        style={{
          // Same safe-area treatment as the library header — in standalone
          // PWA mode the iOS status bar sits over the page.
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
          paddingBottom: "12px",
          paddingLeft: "calc(env(safe-area-inset-left, 0px) + 20px)",
          paddingRight: "calc(env(safe-area-inset-right, 0px) + 20px)",
        }}
      >
        <Link
          to="/library"
          className="pv-border pv-fg rounded-md border px-3 py-2 text-sm font-medium active:scale-95 hover:opacity-80"
          aria-label="Back to library"
        >
          ‹ Library
        </Link>
        <div className="flex flex-col leading-tight tracking-tight">
          <div
            className="pv-fg text-2xl font-bold"
            style={{ fontFamily: '"Iowan Old Style", Georgia, serif' }}
          >
            Classics
          </div>
          <div className="pv-fg-soft text-xs">
            Free public-domain books · Project Gutenberg
          </div>
        </div>
      </header>

      {/* Search + subject chips */}
      <div className="px-5 pt-4">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by title or author…"
          className="pv-surface-2 pv-border pv-fg w-full rounded-md border px-4 py-2.5 text-base outline-none placeholder:opacity-50"
        />
        {subjects.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            <SubjectChip
              label="All"
              active={subject === null}
              onClick={() => setSubject(null)}
            />
            {subjects.map((s) => (
              <SubjectChip
                key={s}
                label={s}
                active={subject === s}
                onClick={() => setSubject(subject === s ? null : s)}
              />
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="mx-5 mt-4 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}
      {!catalog && !error && (
        <div className="pv-fg-soft mx-5 mt-6 text-sm">Loading catalog…</div>
      )}
      {catalog && visible.length === 0 && (
        <div className="pv-fg-soft mx-5 mt-6 text-sm">
          No books match {query.trim() ? `“${query.trim()}”` : "that filter"}.
        </div>
      )}

      {/* Pinned: original-language editions to pair with the Translate
          feature. Rendered above the main grid so they're impossible to
          miss, with a language badge on every card. */}
      {multilingual.length > 0 && (
        <section className="px-5 pt-5">
          <div
            className="pv-fg text-lg font-bold"
            style={{ fontFamily: '"Iowan Old Style", Georgia, serif' }}
          >
            🌍 In the Original — try Translate
          </div>
          <div className="pv-fg-soft mt-0.5 text-xs">
            Classics in their original language. Add one, then translate as
            you read.
          </div>
          <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {multilingual.map((entry) => (
              <ClassicCard
                key={entry.id}
                entry={entry}
                inLibrary={libraryFileNames?.has(classicFileName(entry)) ?? false}
                busy={busyIds.has(entry.id)}
                failed={failedId === entry.id}
                onToggle={onToggle}
              />
            ))}
          </div>
        </section>
      )}

      <div className="grid grid-cols-2 gap-4 p-5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
        {english.map((entry) => (
          <ClassicCard
            key={entry.id}
            entry={entry}
            inLibrary={libraryFileNames?.has(classicFileName(entry)) ?? false}
            busy={busyIds.has(entry.id)}
            failed={failedId === entry.id}
            onToggle={onToggle}
          />
        ))}
      </div>
    </div>
  );
}

function SubjectChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "pv-accent-bg rounded-full px-3 py-1.5 text-xs font-semibold"
          : "pv-surface-2 pv-border pv-fg-soft rounded-full border px-3 py-1.5 text-xs hover:opacity-80"
      }
    >
      {label}
    </button>
  );
}

function ClassicCard({
  entry,
  inLibrary,
  busy,
  failed,
  onToggle,
}: {
  entry: ClassicEntry;
  inLibrary: boolean;
  busy: boolean;
  failed: boolean;
  onToggle: (entry: ClassicEntry, inLibrary: boolean) => void;
}) {
  const [coverBroken, setCoverBroken] = useState(false);
  const mb = entry.sizeBytes / 1024 / 1024;
  const sizeLabel = mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(entry.sizeBytes / 1024))} KB`;
  // Original-language editions show their title in the original language
  // plus a loud native-language badge ("DEUTSCH", "FRANÇAIS", "日本語") —
  // the native half of langLabel ("Deutsch / German"), uppercased.
  const displayTitle = entry.originalTitle ?? entry.title;
  const langBadge = entry.langLabel
    ? entry.langLabel.split("/")[0].trim().toUpperCase()
    : null;

  return (
    <div className="pv-surface pv-border relative flex flex-col rounded-md border p-3 shadow-sm">
      {langBadge && (
        <span className="pv-accent-bg absolute left-2 top-2 z-10 rounded px-1.5 py-0.5 text-[10px] font-bold tracking-widest shadow">
          {langBadge}
        </span>
      )}
      <div className="flex flex-1 flex-col items-center gap-3">
        <div className="pv-surface-2 flex h-48 w-32 items-center justify-center overflow-hidden rounded shadow-inner">
          {entry.cover && !coverBroken ? (
            <img
              src={classicsUrl(entry.cover)}
              alt={displayTitle}
              loading="lazy"
              className="h-full w-full object-cover"
              draggable={false}
              onError={() => setCoverBroken(true)}
            />
          ) : (
            <FallbackCover title={displayTitle} author={entry.author} />
          )}
        </div>
        <div className="w-full text-center">
          <div className="pv-fg line-clamp-2 text-base font-semibold">
            {displayTitle}
          </div>
          {entry.author && (
            <div className="pv-fg-soft line-clamp-1 text-sm">{entry.author}</div>
          )}
          <div className="pv-fg-soft mt-0.5 text-xs tabular-nums">{sizeLabel}</div>
        </div>
      </div>

      <button
        type="button"
        disabled={busy}
        onClick={() => onToggle(entry, inLibrary)}
        aria-label={
          inLibrary
            ? `Remove ${displayTitle} from library`
            : `Add ${displayTitle} to library`
        }
        className={
          "mt-3 w-full rounded-md px-3 py-2 text-sm font-semibold active:scale-95 " +
          (inLibrary
            ? "pv-surface-2 pv-border pv-fg border hover:opacity-80"
            : "pv-accent-bg hover:opacity-90") +
          (busy ? " opacity-60" : "")
        }
      >
        {busy ? (
          <span className="inline-flex items-center justify-center gap-2">
            <span
              aria-hidden
              className="inline-block"
              style={{ animation: "pv-classics-spin 0.9s linear infinite" }}
            >
              ⟳
            </span>
            {inLibrary ? "Removing…" : "Adding…"}
          </span>
        ) : inLibrary ? (
          "✓ In library"
        ) : (
          "＋ Add"
        )}
      </button>
      {failed && (
        <div className="mt-1 text-center text-xs text-red-400">
          Something went wrong — try again.
        </div>
      )}
      <style>{`@keyframes pv-classics-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}

/** Same procedural fallback cover the library uses for cover-less books —
 *  deterministic colour from the title, serif title, italic author. */
function FallbackCover({ title, author }: { title: string; author?: string }) {
  const palette = ["#3b3b3b", "#5b4636", "#4a3a52", "#3a4a52", "#52473a", "#3a523f"];
  let h = 0;
  for (let i = 0; i < title.length; i++) h = ((h << 5) - h + title.charCodeAt(i)) | 0;
  const bg = palette[Math.abs(h) % palette.length];
  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-2 p-3 text-center"
      style={{ background: bg, color: "#f5f0e6" }}
    >
      <span className="font-serif text-sm leading-tight" style={{ wordBreak: "break-word" }}>
        {title}
      </span>
      <span
        className="text-[10px] italic leading-tight opacity-80"
        style={{ wordBreak: "break-word" }}
      >
        {author || " "}
      </span>
    </div>
  );
}
